import apiClient from './apiClient';
import { Member, ApiResponse, ApiError } from '../types/api';

export class MemberService {
  /**
   * Get all members
   * @returns Promise<Member[]> - Array of members
   */
  static async getMembers(): Promise<Member[]> {
    try {
      const response = await apiClient.get<ApiResponse<Member[]> | Member[]>('/members');
      // Handle both wrapped and direct array responses
      if (Array.isArray(response.data)) {
        return response.data;
      }
      return (response.data as ApiResponse<Member[]>).data;
    } catch (error) {
      throw error as ApiError;
    }
  }

  /**
   * Get allocatable members with the week's billable utilisation attached.
   * This is what the Team Allocation picker offers: every active member holding
   * a delivery role, whether or not they have allocation data for the week.
   * @returns members plus the year/week the utilisation figures came from
   */
  static async getAvailableMembers(): Promise<{
    members: Member[];
    actualYear?: number;
    actualWeek?: number;
  }> {
    try {
      const response = await apiClient.get<any>('/members/available?all=true');
      const body = response.data;
      return {
        members: Array.isArray(body) ? body : body?.data || [],
        actualYear: body?.actual_year,
        actualWeek: body?.actual_week,
      };
    } catch (error) {
      throw error as ApiError;
    }
  }

  /**
   * Get fully benched employees - people with no allocated hours for the week.
   * Kept for reporting; the Team Allocation picker uses getAvailableMembers().
   * @returns members plus the year/week the bench data actually came from
   */
  static async getBenchedMembers(): Promise<{
    members: Member[];
    actualYear?: number;
    actualWeek?: number;
  }> {
    try {
      const response = await apiClient.get<any>('/members/benched');
      const body = response.data;
      return {
        members: Array.isArray(body) ? body : body?.data || [],
        actualYear: body?.actual_year,
        actualWeek: body?.actual_week,
      };
    } catch (error) {
      throw error as ApiError;
    }
  }

  /**
   * Get a specific member by ID
   * @param memberId - The ID of the member to fetch
   * @returns Promise<Member> - Member details
   */
  static async getMember(memberId: number): Promise<Member> {
    try {
      const response = await apiClient.get<ApiResponse<Member>>(`/members/${memberId}`);
      return response.data.data;
    } catch (error) {
      throw error as ApiError;
    }
  }

  /**
   * Search members by name or email
   * @param searchTerm - Search term for name or email
   * @returns Promise<Member[]> - Array of matching members
   */
  static async searchMembers(searchTerm: string): Promise<Member[]> {
    try {
      const response = await apiClient.get<ApiResponse<Member[]> | Member[]>(`/members/search?q=${encodeURIComponent(searchTerm)}`);
      // Handle both wrapped and direct array responses
      if (Array.isArray(response.data)) {
        return response.data;
      }
      return (response.data as ApiResponse<Member[]>).data;
    } catch (error) {
      throw error as ApiError;
    }
  }

  /**
   * Create a new member
   * @param memberData - Member data to create
   * @returns Promise<Member> - Created member
   */
  static async createMember(memberData: Partial<Member>): Promise<Member> {
    try {
      const response = await apiClient.post<ApiResponse<Member>>('/members', memberData);
      return response.data.data;
    } catch (error) {
      throw error as ApiError;
    }
  }

  /**
   * Update an existing member
   * @param memberId - The ID of the member to update
   * @param memberData - Updated member data
   * @returns Promise<Member> - Updated member
   */
  static async updateMember(memberId: number, memberData: Partial<Member>): Promise<Member> {
    try {
      const response = await apiClient.put<ApiResponse<Member>>(`/members/${memberId}`, memberData);
      return response.data.data;
    } catch (error) {
      throw error as ApiError;
    }
  }

  /**
   * Delete a member
   * @param memberId - The ID of the member to delete
   * @returns Promise<void>
   */
  static async deleteMember(memberId: number): Promise<void> {
    try {
      await apiClient.delete(`/members/${memberId}`);
    } catch (error) {
      throw error as ApiError;
    }
  }
}
